import { Component, ElementRef, OnDestroy, OnInit, ViewChild, signal, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { AssetsService } from '@app/shared/services/assets.service';
import { SharedModule } from '@app/shared/shared.module';
import { EventManager } from '@app/shared/utils/event-manager';
import { TranslateService } from '@ngx-translate/core';
import { TournamentRankingListComponent } from './tournament-ranking-list/tournament-ranking-list.component';

import { User } from '@app/shared/models/user.model';
import { LoadingComponent } from '@app/shared/components/loading/loading.component';
import { TournamentsService } from '@app/shared/modals/live-ops/tournaments/tournaments.service';
import { TournamentRanking, TournamentsRankings } from '../tournaments.model';
import { lastValueFrom, Observable, takeUntil } from 'rxjs';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { AppService } from '@app/app.service';
import { EventsRepository } from '@app/shared/state/events/events.repository';
import { UserRepository } from '@app/shared/state/user/user.repository';
import { GlobalDataRepository } from '@app/shared/state/global-data/global-data.repository';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { UtilsService } from '@app/shared/services/utils.service';
import { TournamentsRankingsRepository } from '@app/shared/state/tournaments-rankings/tournaments-rankings.repository';
import { Reward } from '@app/shared/models/wallet.model';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-tournament-ranking',
  templateUrl: './tournament-ranking.component.html',
  styleUrls: ['./tournament-ranking.component.scss'],
  standalone: true,
  imports: [AnimatedTimerComponent, TournamentRankingListComponent, LoadingComponent, ButtonComponent, SharedModule],
})
export class TournamentRankingComponent implements OnInit, OnDestroy {
  tournamentRankingModalData = inject<Modal>(MAT_DIALOG_DATA);
  private eventManager = inject(EventManager);
  private eventsRepo = inject(EventsRepository);
  private translateService = inject(TranslateService);
  private assetsService = inject(AssetsService);
  private userRepo = inject(UserRepository);
  private tournamentsService = inject(TournamentsService);
  private appService = inject(AppService);
  private globalDatarepo = inject(GlobalDataRepository);
  private utilsService = inject(UtilsService);
  private tournamentsRankingsRepo = inject(TournamentsRankingsRepository);

  @ViewChild('mainContainer', { static: false }) mainContainerRef!: ElementRef;
  @ViewChild('hiddenUser', { static: false }) hiddenUserRef!: ElementRef;
  @ViewChild('mainUserSticky', { read: ElementRef }) mainUserStickyRef!: ElementRef;

  loadingRanking = signal(true);
  lang: string;
  gameId: string;
  gameIcon: unknown;
  buttonText: string;

  event: LimitedTimeEvent;
  rankingList = signal<TournamentRanking[]>([]);
  rankingPrizeList = signal<Reward[]>([]);

  private previousPositions = new Map<string, number>();

  private readonly WAIT_LOADING = 100;

  private observer: IntersectionObserver;
  private componentDestroy: () => Observable<unknown>;

  get user(): User {
    return this.userRepo.getUser();
  }

  async ngOnInit(): Promise<void> {
    this.event = this.eventsRepo.getEventById(this.tournamentRankingModalData.data.eventId);
    this.lang = this.translateService.currentLang;
    this.gameId = this.event.liveOpsStatus.challengeStatus.gameId;

    if (this.gameId) {
      this.gameIcon = {
        'background-image': `url('${this.assetsService.getGameIconUrl(this.gameId)}')`,
      };
    }

    this.buttonText = this.getButtonText();

    await this.getRanking();

    this.tournamentsRankingsRepo.tournamentsRankings$.pipe(takeUntil(this.componentDestroy())).subscribe((rankings) => {
      const tournamentRanking = rankings.find((r) => r.eventId === this.event.eventId);

      if (!tournamentRanking) return;

      this.updateRankingPrizeList(tournamentRanking.prizes);
      this.updateStaticRankingList(tournamentRanking);
    });

    this.loadingRanking.set(false);

    setTimeout(() => {
      this.initStickyObserver();
    }, this.WAIT_LOADING);
  }

  openInfo(): void {
    this.eventManager.openModal(new Modal(NonPersistedModalName.TournamentInfoComponent, ModalType.View, this.event));
  }

  openUserProfile(userId: string): void {
    this.eventManager.openModal(
      new Modal(NonPersistedModalName.PublicProfileComponent, ModalType.View, { userId }, 'big-dialog')
    );
  }

  handleRankingButtonClick(): void {
    if (this.gameId && this.globalDatarepo.getCurrentGame() !== this.gameId) {
      this.appService.showGame(this.gameId);
    } else {
      this.closeModal();
    }
  }

  closeModal(): void {
    this.eventManager.closeModal(
      new Modal(this.tournamentRankingModalData.id, ModalType.View, this.tournamentRankingModalData)
    );
  }

  private async getRanking(): Promise<void> {
    await lastValueFrom(
      this.tournamentsService.getTournamentRanking(this.event.eventId, this.event.liveOpsStatus.hallId)
    );
  }

  private getButtonText(): string {
    if (this.gameId) {
      return 'home.buttons.play';
    } else {
      return 'home.buttons.accept';
    }
  }

  private initStickyObserver(): void {
    const hidden = this.hiddenUserRef?.nativeElement;
    const container = this.mainContainerRef?.nativeElement;
    const sticky = this.mainUserStickyRef?.nativeElement;

    if (!hidden || !container) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          sticky.classList.remove('sticky-top', 'sticky-bottom');
        } else {
          this.handleStickyPositionChange();
        }
      },
      {
        root: container,
        threshold: 1,
      }
    );

    this.observer.observe(hidden);
  }

  private handleStickyPositionChange(): void {
    const container = this.mainContainerRef?.nativeElement;
    const hiddenUser = this.hiddenUserRef?.nativeElement;
    const target = this.mainUserStickyRef?.nativeElement;

    const hiddenRect = hiddenUser?.getBoundingClientRect();
    const containerRect = container?.getBoundingClientRect();

    const hiddenTop = hiddenRect?.top;
    const hiddenBottom = hiddenRect?.bottom;

    const containerTop = containerRect?.top;
    const containerBottom = containerRect?.bottom;

    const isAbove = hiddenTop <= containerTop;
    const isBelow = hiddenBottom >= containerBottom;

    target?.classList.remove('sticky-top', 'sticky-bottom');

    if (isAbove) {
      target?.classList.add('sticky-top');
    } else if (isBelow) {
      target?.classList.add('sticky-bottom');
    }
  }

  // Used to avoid rebuilding DOM when update ranking
  private updateStaticRankingList(updateRanking: TournamentsRankings): void {
    let rankingChanged = false;
    const currentList = [...this.rankingList()];
    const existingBeforeById = new Map(currentList.map((r) => [r.userId, r]));

    for (const ranking of updateRanking.rankings) {
      const existing = existingBeforeById.get(ranking.userId);
      const playerHasChanged = !existing || !this.utilsService.haveObjectsChanged(existing, ranking);

      if (playerHasChanged) {
        const index = currentList.findIndex((r) => r.userId === ranking.userId);

        if (index !== -1) {
          currentList[index] = { ...ranking };
        } else {
          currentList.push({ ...ranking });
        }

        rankingChanged = true;
      }
    }

    if (rankingChanged) {
      currentList.sort((a, b) => b.rankingScore - a.rankingScore);
      this.rankingList.set(currentList);
      this.setPlayerChangePosition();
    }
  }

  private updateRankingPrizeList(rankingPrizeList: Reward[]): void {
    this.rankingPrizeList.set(rankingPrizeList);
  }

  private setPlayerChangePosition(): void {
    this.rankingList().forEach((player, index) => {
      const prevIndex = this.previousPositions.get(player.userId);
      const currentIndex = index;

      if (prevIndex) {
        if (currentIndex < prevIndex) {
          player.positionChange = 'up';
        } else if (currentIndex > prevIndex) {
          player.positionChange = 'down';
        } else {
          player.positionChange = null;
        }
      }

      this.previousPositions.set(player.userId, currentIndex);
    });
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}
